/**
 * Create Listing — Validation Mixin
 *
 * Step-level validation and step state computation.
 *
 * @package Lovasok_Marketplace
 */

export function validationMixin() {
    return {
        validationErrors: [],

        validateStep() {
            this.validationErrors = this.getValidationErrorsForStep(this.currentStep);
            return this.validationErrors.length === 0;
        },

        getValidationErrorsForStep(stepNumber) {
            const errors = [];

            switch (stepNumber) {
                case 1:
                    if (!this.formData.htipus)                       errors.push('A hirdetés típusa kötelező');
                    if (this.listingMode !== 'horseface' && !this.formData.hirdetes_cim.trim())
                        errors.push('A hirdetés címe kötelező');
                    if (this.formData.hirdetes_cim.length > 50)      errors.push('A hirdetés címe max. 50 karakter');
                    if (this.formData.lo_neve.length > 50)           errors.push('A ló neve max. 50 karakter');
                    if (!this.formData.fajta_id)                     errors.push('A fajta kiválasztása kötelező');
                    if (!this.formData.nem_id)                       errors.push('A nem kiválasztása kötelező');
                    if (this.formData.htipus === 'fedezomen' && this.formData.nem_id !== 'stallion')
                        errors.push('Fedezőménnél csak mén adható meg');
                    if (!this.formData.foszin_id)                    errors.push('A szín kiválasztása kötelező');
                    if (!this.formData.datum_ev)                     errors.push('A születési év kötelező');
                    if (!this.formData.datum_ho)                     errors.push('A születési hónap kötelező');
                    if (!this.formData.magassag_bot && !this.formData.magassag_szalag)
                        errors.push('A marmagasság megadása kötelező (bottal vagy szalaggal)');
                    if (!this.formData.kepzettseg_id)                errors.push('A képzettség kiválasztása kötelező');
                    break;

                case 2:
                    if (this.formData.versenyez === '1' && !this.formData.startszam)
                        errors.push('Versenyző ló esetén a startszám kötelező');
                    if (!this.formData.erv_loutlevel)
                        errors.push('A lóútlevél státusz kötelező');
                    if (!this.formData.tisztaveru)
                        errors.push('A származás típusa kötelező');
                    if ((this.formData.htipus === 'fedezomen' || this.formData.tisztaveru === 'I') && !this.formData.apja)
                        errors.push('Tisztavérű vagy fedezőmén esetén az apja kötelező');
                    if ((this.formData.htipus === 'fedezomen' || this.formData.tisztaveru === 'I') && !this.formData.anyja)
                        errors.push('Tisztavérű vagy fedezőmén esetén az anyja kötelező');
                    if (this.formData.htipus === 'fedezomen' && !this.formData.torzskonyvi_szam)
                        errors.push('Fedezőménnél a törzskönyvi szám kötelező');
                    if (this.formData.htipus === 'fedezomen' && !this.formData.alkalmassag.includes('tenyesztes'))
                        errors.push('Fedezőménnél kötelező a "Tenyésztésre alkalmas" alkalmasság');
                    break;

                case 3:
                    if (this.listingMode === 'horseface') {
                        // HorseFace: kép opcionális
                        if (!this.isValidYouTubeUrl(this.formData.video_url) ||
                            !this.isValidYouTubeUrl(this.formData.video_url_2) ||
                            !this.isValidYouTubeUrl(this.formData.video_url_3))
                            errors.push('Csak érvényes YouTube link adható meg videóként');
                        break;
                    }
                    if (this.formData.ar_st === 'ar' && (!this.formData.ar || parseInt(this.formData.ar) <= 0))
                        errors.push('Az ár megadása kötelező');
                    if (this.formData.ar_st === 'ar' && this.formData.htipus === 'elado' && this.formData.ar_tipus === 'fedar')
                        errors.push('Eladó lónál nem választható fedezési díj');
                    if (this.formData.ar_st === 'ar' && this.formData.htipus === 'fedezomen' && this.formData.ar_tipus === 'parban')
                        errors.push('Fedezőménnél nem választható párban ár');
                    if (this.formData.ar_st === 'arkat' && !this.formData.arkatlista)
                        errors.push('Ár kategória esetén a kategória kiválasztása kötelező');
                    if (!this.formData.megtekint_orszag)
                        errors.push('Az ország kiválasztása kötelező');
                    if (this.formData.megtekint_orszag === 'HU' && !this.formData.megtekint_megye)
                        errors.push('A megye kiválasztása kötelező');
                    break;

                case 4:
                    if (this.listingMode === 'horseface') {
                        if (!this.formData.horsefacePackage)
                            errors.push('Kérjük válassz előfizetési csomagot (Havi vagy Éves).');
                        break;
                    }
                    if (this.previewImages.length < 1)  errors.push('Minimum 1 kép feltöltése kötelező');
                    if (this.previewImages.length > 10) errors.push('Maximum 10 kép tölthető fel');
                    if (!this.isValidYouTubeUrl(this.formData.video_url) ||
                        !this.isValidYouTubeUrl(this.formData.video_url_2) ||
                        !this.isValidYouTubeUrl(this.formData.video_url_3))
                        errors.push('Csak érvényes YouTube link adható meg videóként');
                    break;

                case 5:
                    // Csomagválasztás akkor kötelező, amikor a csomag lépés tényleg
                    // látszik: új hirdetésnél, vagy draft (pl. HorseFace-ből átalakított,
                    // még nem publikált) hirdetés szerkesztésekor. Publikált hirdetés
                    // szerkesztésekor a lépés kimarad, így itt nem validálunk.
                    if (this.listingMode === 'elado' && !this.skipsPackageStepOnEdit) {
                        const pkg = this.formData.package;
                        if (pkg === '' || pkg === null || pkg === undefined) {
                            errors.push('Kérjük válassz kiemelési csomagot vagy az ingyenes hirdetést');
                        }
                    }
                    break;

                case 6:
                    if (!this.formData.fajta_id || !this.formData.nem_id || !this.formData.foszin_id)
                        errors.push('Hiányzó alapadatok — menj vissza az 1. lépéshez');
                    if (this.previewImages.length < 1)  errors.push('Minimum 1 kép szükséges');
                    if (this.previewImages.length > 10) errors.push('Maximum 10 kép tölthető fel');
                    break;
            }

            return errors;
        },

        getStepState(stepNumber) {
            if (stepNumber === this.currentStep) return 'current';
            if (stepNumber > this.currentStep)  return 'upcoming';
            return this.getValidationErrorsForStep(stepNumber).length > 0 ? 'error' : 'complete';
        },

        isValidYouTubeUrl(url) {
            if (!url || !String(url).trim()) return true;
            const raw = String(url).trim();
            const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
            try {
                const parsed = new URL(normalized);
                const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
                if (host === 'youtu.be') return parsed.pathname.length > 1;
                if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
                    if (parsed.pathname.startsWith('/watch')) return !!parsed.searchParams.get('v');
                    if (parsed.pathname.startsWith('/embed/')) return parsed.pathname.length > 7;
                    if (parsed.pathname.startsWith('/shorts/')) return parsed.pathname.length > 8;
                }
                return false;
            } catch {
                return false;
            }
        },
    };
}
